import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Typography, message, Popconfirm, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const { Title } = Typography;

interface User {
  id: string;
  name: string;
  email: string;
  role: 'admin' | 'operator' | 'staff';
  created_at: string;
}

const UserList: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<User[]>([]);
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/users`);
      if (response.data.success) {
        setUsers(response.data.data);
      } else {
        message.error(response.data.message || '获取用户列表失败');
      }
    } catch (error: any) {
      message.error('获取用户列表时出错: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleDelete = async (id: string) => {
    try {
      const response = await axios.delete(`${process.env.REACT_APP_API_URL}/api/users/${id}`);
      if (response.data.success) {
        message.success('用户删除成功');
        fetchUsers();
      } else {
        message.error(response.data.message || '删除用户失败');
      }
    } catch (error: any) {
      message.error('删除用户时出错: ' + (error.response?.data?.message || error.message));
    }
  };

  const getRoleTag = (role: string) => {
    let color = '';
    let text = '';
    
    switch (role) {
      case 'admin':
        color = 'red';
        text = '管理员';
        break;
      case 'operator':
        color = 'green';
        text = '运营';
        break;
      case 'staff':
        color = 'blue';
        text = '员工';
        break;
      default:
        color = 'default';
        text = role;
    }
    
    return <Tag color={color}>{text}</Tag>;
  };

  const columns = [
    {
      title: '用户名',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '邮箱',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: '角色',
      dataIndex: 'role',
      key: 'role',
      render: (role: string) => getRoleTag(role),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text: string) => new Date(text).toLocaleString('zh-CN'),
    },
    {
      title: '操作',
      key: 'action',
      render: (_: any, record: User) => (
        <Space size="middle" className="table-actions">
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => navigate(`/users/${record.id}/edit`)}
            disabled={record.id === currentUser?.id}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定删除此用户吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
            disabled={record.id === currentUser?.id}
          >
            <Button 
              type="primary" 
              danger 
              icon={<DeleteOutlined />} 
              disabled={record.id === currentUser?.id}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (currentUser?.role !== 'admin') {
    return (
      <div>
        <Title level={2}>用户管理</Title>
        <div style={{ padding: 24, background: '#fff' }}>
          <p>抱歉，您没有权限访问此页面。只有管理员可以管理用户。</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={2}>用户管理</Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate('/users/new')}
        >
          添加用户
        </Button>
      </div>
      <Table
        loading={loading}
        columns={columns}
        dataSource={users}
        rowKey="id"
        pagination={{ pageSize: 10 }}
      />
    </div>
  );
};

export default UserList; 